/** Scatter-triggered free spins feature (12 spins, escalating wilds) */

import { Container } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { FREE_GAME_STRIPS } from '../config/ReelStrips';
import {
  FREE_GAME_SPINS,
  FREE_GAME_REEL_COUNT,
  RUSH_FEVER_FREE_GAME_MULTIPLIER,
  ESCALATING_WILD_WEIGHTS,
} from '../config/FeatureConfig';
import { ClientSpinResultGenerator, SpinResult } from '../core/SpinResultGenerator';
import { WinEvaluator } from '../core/WinEvaluator';
import { WILD_SYMBOL } from '../config/PayTable';
import { SpineManager } from '../animations/SpineManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('FreeGamesFeature');

export interface FreeGamesState {
  totalSpins: number;
  currentSpin: number;
  totalWin: number;
  isActive: boolean;
  rushMultiplier: number;
}

export interface FreeSpinResult {
  spinNumber: number;
  spinResult: SpinResult;
  wildOverlayPositions: [number, number][];
  spinWin: number;
  runningTotal: number;
}

/** Manages the Free Games bonus feature */
export class FreeGamesFeature {
  private _state: FreeGamesState = {
    totalSpins: 0,
    currentSpin: 0,
    totalWin: 0,
    isActive: false,
    rushMultiplier: 1,
  };

  private _spinGenerator: ClientSpinResultGenerator;

  constructor(spinGenerator?: ClientSpinResultGenerator) {
    this._spinGenerator = spinGenerator ?? new ClientSpinResultGenerator();
  }

  get state(): Readonly<FreeGamesState> {
    return this._state;
  }

  get isActive(): boolean {
    return this._state.isActive;
  }

  get currentSpin(): number {
    return this._state.currentSpin;
  }

  get totalSpins(): number {
    return this._state.totalSpins;
  }

  get totalWin(): number {
    return this._state.totalWin;
  }

  get rushMultiplier(): number {
    return this._state.rushMultiplier;
  }

  /** Check if free games should trigger based on scatter count */
  canTrigger(scatterCount: number): boolean {
    return scatterCount >= 3;
  }

  /** Check if all free spins have been used */
  isComplete(): boolean {
    return this._state.currentSpin >= this._state.totalSpins;
  }

  /**
   * Start the free games feature.
   * Initializes state for 12 free spins with 2x rush multiplier.
   * The caller (main.ts) is responsible for switching reelSet mode and running spins.
   */
  start(): void {
    this._state = {
      totalSpins: FREE_GAME_SPINS,
      currentSpin: 0,
      totalWin: 0,
      isActive: true,
      rushMultiplier: RUSH_FEVER_FREE_GAME_MULTIPLIER,
    };

    logger.info(
      `Free Games started: ${FREE_GAME_SPINS} spins, ` +
      `${FREE_GAME_REEL_COUNT} reels, ${RUSH_FEVER_FREE_GAME_MULTIPLIER}x rush multiplier`
    );
  }

  /**
   * Get the escalating wild weight for the given spin number (1-indexed).
   * Returns { wildWeight, notWildWeight } for probability calculation.
   */
  getEscalatingWildWeight(spinNumber: number): { wildWeight: number; notWildWeight: number; probability: number } {
    const index = Math.max(0, Math.min(spinNumber - 1, ESCALATING_WILD_WEIGHTS.length - 1));
    const [wildWeight, notWildWeight] = ESCALATING_WILD_WEIGHTS[index];
    const probability = wildWeight / (wildWeight + notWildWeight);

    logger.debug(
      `Spin ${spinNumber} wild weight: ${wildWeight}/${wildWeight + notWildWeight} ` +
      `(${(probability * 100).toFixed(2)}%)`
    );

    return { wildWeight, notWildWeight, probability };
  }

  /**
   * Execute one free spin. Generates a result using FG strips,
   * applies escalating wild overlay, evaluates wins, decrements counter.
   *
   * @param totalBet The total bet from the triggering spin
   * @returns FreeSpinResult with all spin data
   */
  doFreeSpin(totalBet: number): FreeSpinResult {
    if (!this._state.isActive) {
      throw new Error('Free games not active — call start() first');
    }
    if (this.isComplete()) {
      throw new Error('All free spins used');
    }

    this._state.currentSpin++;
    const spinNumber = this._state.currentSpin;

    logger.info(`--- Free Spin ${spinNumber}/${this._state.totalSpins} ---`);

    // 1. Generate spin result using FG reel strips (5 reels only)
    const fgStrips = FREE_GAME_STRIPS.slice(0, FREE_GAME_REEL_COUNT);
    const spinResult = this._spinGenerator.generate(fgStrips, {
      isBaseGame: false,
      freeSpinIndex: spinNumber,
      totalBet,
    });

    // 2. Apply escalating wild overlay to the grid
    const wildOverlayPositions = this._applyEscalatingWilds(spinResult.grid, spinNumber);

    // 3. Re-evaluate wins after wild overlay (the grid has been mutated)
    const wins = WinEvaluator.evaluate(spinResult.grid, {
      isBaseGame: false,
      totalBet,
    });
    const spinWin = WinEvaluator.calculateTotalWin(wins, totalBet);

    // Update spin result with re-evaluated wins
    spinResult.wins = wins;
    spinResult.totalWin = spinWin;

    // 4. Accumulate total win
    this._state.totalWin += spinWin;

    logger.info(
      `Free Spin ${spinNumber} result: win=$${spinWin.toFixed(4)}, ` +
      `wildOverlays=${wildOverlayPositions.length}, ` +
      `runningTotal=$${this._state.totalWin.toFixed(4)}`
    );

    // 5. Check if complete
    if (this.isComplete()) {
      logger.info(
        `Free Games complete! Total win: $${this._state.totalWin.toFixed(4)}`
      );
    }

    return {
      spinNumber,
      spinResult,
      wildOverlayPositions,
      spinWin,
      runningTotal: this._state.totalWin,
    };
  }

  /**
   * Apply escalating wild overlay to the grid.
   * For each non-WILD, non-SCATTER, non-Rush cell on reels 2/3/4 (indices 1,2,3),
   * roll the escalating wild probability. If hit, replace the symbol with WILD.
   *
   * @returns Array of [reel, row] positions where wilds were added
   */
  private _applyEscalatingWilds(grid: string[][], spinNumber: number): [number, number][] {
    const { probability } = this.getEscalatingWildWeight(spinNumber);
    const overlayPositions: [number, number][] = [];

    // WILD overlay only applies to reels 2, 3, 4 (indices 1, 2, 3)
    const wildReelIndices = [1, 2, 3];

    for (const reelIdx of wildReelIndices) {
      if (reelIdx >= grid.length) continue;

      for (let rowIdx = 0; rowIdx < grid[reelIdx].length; rowIdx++) {
        const currentSymbol = grid[reelIdx][rowIdx];

        // Skip if already WILD, SCATTER, or Rush
        if (currentSymbol === WILD_SYMBOL || currentSymbol === 'SCATTER' || currentSymbol === 'Rush') {
          continue;
        }

        // Roll for wild overlay
        if (Math.random() < probability) {
          grid[reelIdx][rowIdx] = WILD_SYMBOL;
          overlayPositions.push([reelIdx, rowIdx]);

          logger.debug(
            `Wild overlay: reel ${reelIdx + 1}, row ${rowIdx} ` +
            `(replaced ${currentSymbol})`
          );
        }
      }
    }

    if (overlayPositions.length > 0) {
      logger.info(
        `Escalating wilds: ${overlayPositions.length} wilds added ` +
        `(probability=${(probability * 100).toFixed(2)}%)`
      );
    }

    return overlayPositions;
  }

  /**
   * End the free games feature.
   * Resets state and signals the caller to switch back to base game.
   * @returns Total win from all free spins
   */
  end(): number {
    const totalWin = this._state.totalWin;

    logger.info(
      `Free Games ended. Total win: $${totalWin.toFixed(4)} ` +
      `across ${this._state.totalSpins} spins`
    );

    this._state.isActive = false;
    return totalWin;
  }

  /** Reset state for next trigger */
  reset(): void {
    this._state = {
      totalSpins: 0,
      currentSpin: 0,
      totalWin: 0,
      isActive: false,
      rushMultiplier: 1,
    };
  }

  // ── Spine popup animations ─────────────────────────────────────────────

  /** Currently active popup Spine instances */
  private _popupBg: Spine | null = null;
  private _popupText: Spine | null = null;

  /**
   * Show the free games intro popup using Spine animations.
   * Plays freegame_intro → freegame_loop on r7s_popup_backgrounds,
   * and the corresponding animation on r7s_popup_text.
   *
   * @returns The popup container (caller can parent it), or null if Spine not available.
   */
  async showIntroPopup(
    parentContainer: Container,
    designW: number,
    designH: number,
  ): Promise<Container | null> {
    const sm = SpineManager.getInstance();
    if (!sm.isLoaded('r7s_popup_backgrounds')) {
      logger.warn('r7s_popup_backgrounds not loaded — skipping intro popup');
      return null;
    }

    const popupContainer = new Container();
    popupContainer.zIndex = 1500;
    popupContainer.x = designW / 2;
    popupContainer.y = designH / 2;
    parentContainer.addChild(popupContainer);

    // Background spine
    const bg = sm.createInstance('r7s_popup_backgrounds');
    if (bg) {
      this._popupBg = bg;
      popupContainer.addChild(bg);
      sm.playStartThenLoop(bg, 'freegame_intro', 'freegame_loop').catch(() => {});
    }

    // Text spine (overlay)
    if (sm.isLoaded('r7s_popup_text')) {
      const txt = sm.createInstance('r7s_popup_text');
      if (txt) {
        this._popupText = txt;
        popupContainer.addChild(txt);
        sm.playStartThenLoop(txt, 'freegame_intro', 'freegame_loop').catch(() => {});
      }
    }

    return popupContainer;
  }

  /**
   * Play the outro animation on the popup, then clean up.
   * Call this before switching to the free spins flow.
   */
  async hidePopup(popupContainer: Container | null): Promise<void> {
    const sm = SpineManager.getInstance();

    if (this._popupBg) {
      try {
        sm.playStartThenLoop(this._popupBg, 'freegame_outro', 'freegame_outro_loop').catch(() => {});
        // Wait a bit for the outro to play before removing
        await new Promise((r) => setTimeout(r, 1000));
      } catch { /* swallow */ }
      sm.returnInstance('r7s_popup_backgrounds', this._popupBg);
      this._popupBg = null;
    }

    if (this._popupText) {
      sm.returnInstance('r7s_popup_text', this._popupText);
      this._popupText = null;
    }

    if (popupContainer) {
      if (popupContainer.parent) {
        popupContainer.parent.removeChild(popupContainer);
      }
      popupContainer.destroy({ children: true });
    }
  }
}
